// validacionAriston.js — reglas de pre-validación de la liquidación Ariston.
//
// Copia de app de netlify/functions/_validacionAriston.js (la que usa
// getAristonLiquidaciones.js para pre-validar al cargar). Son dos archivos
// porque CRA no deja importar fuera de src/ y las funciones de Netlify son
// CommonJS — validacionAriston.test.js compara las dos con los mismos casos
// y falla si alguien cambia una regla en un solo lado.
//
// Se usan en dos sitios:
//  - LiquidacionesView.js: validar una orden suelta y exportar el CSV.
//  - Cierre de ruta del técnico (validateOrders en TechnicianOrdersView.js):
//    una garantía que va a pasar a "Pre-liquidación Ariston" tiene que
//    cumplirlas ya, mientras el técnico todavía puede corregirlas — antes
//    oficina se enteraba días después, al pre-validar en Liquidaciones.

export const MARCAS_VALIDAS = ['ARISTON', 'FLECK', 'CHAFFOTEAUX', 'NEUTROS', 'CHROMAGEN'];
const TIPOS_ACTIVIDAD_VALIDOS = ['1', '2', '5', '7'];

export const esMarcaAriston = (m) => !!m && /ariston/i.test(m.trim());
export const esMarcaValida  = (m) => { if (!m) return false; const u = m.trim().toUpperCase(); return MARCAS_VALIDAS.includes(u) || esMarcaAriston(m); };
export const normalizarNumeroSerie = (s) => { if (!s) return s; const n = String(s).trim().replace(/^0+/, ''); return n || '0'; };

export const esPuestaEnMarcha = (texto) => {
  if (!texto) return false;
  const n = texto.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
  return /puesta\s*en\s*marcha/.test(n) || /puesta\s*marcha/.test(n) || /\bp\s*e\s*m\b/.test(n) || /psta\s*en\s*marcha/.test(n) || /puest\s*en\s*marcha/.test(n);
};

// Firma, activo vinculado, líneas y tipo de actividad — sin mirar el activo.
export const validarOrdenRapida = (orden) => {
  const errores = [];
  if (!orden.signed) errores.push('No firmada');
  if (!orden.assets?.length) { errores.push('Sin activos'); return errores; }
  if (!orden.lines?.length) {
    if (!esPuestaEnMarcha(orden['private-comments'])) errores.push('Sin líneas');
  } else {
    const primeraLinea = orden.lines.find(l => l['line-type'] === 'ITEM' && !l.deleted && !l['item-deleted']);
    if (!primeraLinea) {
      if (!esPuestaEnMarcha(orden['private-comments'])) errores.push('Sin líneas válidas');
    } else {
      const tipo = primeraLinea['item-reference'];
      if (!TIPOS_ACTIVIDAD_VALIDOS.includes(tipo) && !String(tipo).startsWith('6'))
        errores.push(`Tipo de actividad "${tipo}" no válido`);
    }
  }
  return errores;
};

// asset: { brand, serialNumber, warrantyStartDate }
export const validarAsset = (asset) => {
  const errores = [];
  if (!asset.brand) errores.push('Sin marca en activo');
  else if (!esMarcaValida(asset.brand)) errores.push(`Marca "${asset.brand}" no válida`);
  if (!asset.serialNumber) {
    errores.push('Sin número de serie');
  } else {
    const serial = normalizarNumeroSerie(asset.serialNumber);
    if (serial.length !== 21) errores.push(`Nº serie inválido: ${serial.length} caracteres (se requieren 21)`);
  }
  if (!asset.warrantyStartDate) errores.push('Sin fecha de inicio de garantía');
  return errores;
};

// Pre-validación completa de una orden. asset = null → el activo no está en
// el espejo de Firebase (todavía sin sincronizar).
export const preValidarOrdenAriston = (orden, asset) => {
  const advertencias = [];
  if (!orden['account-path']) advertencias.push('Sin account-path');
  const erroresRapidos = validarOrdenRapida(orden);
  if (erroresRapidos.length > 0) return { valida: false, errores: erroresRapidos, advertencias };
  if (!asset) return { valida: false, errores: ['Activo no sincronizado en Firebase — abre la orden y pulsa Validar'], advertencias };
  const erroresAsset = validarAsset(asset);
  return { valida: erroresAsset.length === 0, errores: erroresAsset, advertencias };
};

// Las mismas reglas, aplicadas a una orden tal como la ve la app de técnicos
// (getTechnicianOrders.js aplana el activo en assetBrand/assetSerialNumber/
// assetWarrantyStartDate). La firma no se comprueba aquí: validateOrders ya
// decide qué hacer con una garantía sin firmar. Cada mensaje dice dónde se
// arregla, porque es el técnico quien tiene que hacerlo.
const DONDE_SE_ARREGLA = [
  [/^Sin activos/,                 'la orden no tiene equipo vinculado — avisa a oficina'],
  [/^Sin líneas|^Tipo de actividad/, 'la primera línea tiene que ser el servicio Ariston'],
  [/marca/i,                       'corrígela en "Editar activo"'],
  [/serie/i,                       'corrígelo en "Editar activo"'],
  [/fecha de inicio de garantía/,  'añádela en "Editar activo"'],
];
// puestaEnMarcha: la orden es una puesta en marcha (resultado de la app, no
// texto en private-comments) — Ariston no le exige línea de servicio.
// omitirLineas: la regla de la línea de servicio la resuelve el cierre de
// ruta preguntando el tipo de intervención (ver necesitaTipoIntervencion).
export const erroresAristonCierreRuta = (order, { puestaEnMarcha = false, omitirLineas = false } = {}) => {
  const sinReglaLineas = puestaEnMarcha || omitirLineas;
  const errores = [
    ...validarOrdenRapida({ ...order, signed: true, ...(sinReglaLineas ? { lines: [], 'private-comments': 'puesta en marcha' } : {}) }),
    ...(order.assets?.length ? validarAsset({
      brand:             order.assetBrand,
      serialNumber:      order.assetSerialNumber,
      warrantyStartDate: order.assetWarrantyStartDate,
    }) : []),
  ];
  return errores.map(e => {
    const pista = DONDE_SE_ARREGLA.find(([re]) => re.test(e));
    return pista ? `${e} — ${pista[1]}` : e;
  });
};

// Tipos de intervención que el técnico puede elegir al cerrar ruta cuando a
// la orden le falta la línea de servicio (o no va la primera). Mismos
// item-id fijos que netlify/functions/anadirLineaServicio.js, que es quien
// la añade — el "5" no tiene artículo propio en StelOrder.
export const TIPOS_INTERVENCION_ARISTON = [
  { tipo: '2', label: 'Garantía completa', desc: 'Mano de obra + piezas' },
  { tipo: '6', label: 'Garantía solo piezas', desc: 'Sin mano de obra' },
  { tipo: '7', label: 'Sustitución del equipo', desc: 'Se cambia el aparato' },
  { tipo: '1', label: 'Puesta en marcha', desc: 'Primer arranque del equipo' },
];

// ¿A la orden le falta la línea de servicio Ariston como primera línea?
// (sin líneas, o la primera es un material). La orden tiene que tener
// equipo: sin él, el error es otro ("Sin activos").
export const necesitaTipoIntervencion = (order) =>
  !!order.assets?.length &&
  validarOrdenRapida({ ...order, signed: true }).some(e => /^Sin líneas|^Tipo de actividad/.test(e));
